import { m } from '@/locale/paraglide/messages';
import { getFooterLinks } from '@/config/footer-config';
import { getSocialLinks } from '@/config/social-config';
import { isLinkActive } from '@/lib/urls';
import { cn } from '@/lib/utils';
import Container from '@/components/layout/container';
import { Logo } from '@/components/shared/logo';
import { useLocation } from '@tanstack/react-router';
import { websiteConfig } from '@/config/website';
import {
  getCanonicalPathname,
  getIndexableLocalesForPath,
  getLocale,
  localeConfig,
  localizeHref,
} from '@/lib/locale';

export function Footer({ className }: React.HTMLAttributes<HTMLElement>) {
  const locale = getLocale();
  const messageOptions = { locale };
  const pathname = useLocation().pathname;
  const footerLinks = getFooterLinks(locale);
  const socialLinks = getSocialLinks();
  // SSR 输出的语言版本链接，让爬虫能从当前页发现其他语言版本
  const canonicalPath = getCanonicalPathname(pathname);
  const languageLocales = getIndexableLocalesForPath(canonicalPath);
  return (
    <footer className={cn('border-t', className)}>
      <Container className="px-4">
        <div className="grid grid-cols-2 gap-8 py-16 md:grid-cols-6 lg:grid-cols-7">
          <div className="col-span-full flex flex-col items-start md:col-span-2">
            <div className="flex items-center space-x-2">
              <Logo />
              <span className="text-xl font-semibold">
                {websiteConfig.metadata?.name}
              </span>
            </div>
            <p className="text-muted-foreground text-base py-2 md:pr-12">
              {m.footer_tagline({}, messageOptions)}
            </p>
            <nav
              aria-label={m.common_social_links({}, messageOptions)}
              className="flex items-center gap-4 pt-6"
            >
              {socialLinks?.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.title}
                    href={link.href ?? '#'}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={link.title}
                    className="inline-flex size-8 items-center justify-center rounded-full border border-border hover:bg-primary/10 hover:text-primary hover:border-primary/30 transition-all duration-200"
                  >
                    {Icon ? <Icon className="size-4" /> : null}
                  </a>
                );
              })}
            </nav>
          </div>

          {footerLinks?.map((section) => (
            <div
              key={section.title}
              className="col-span-1 md:col-span-1 flex flex-col items-start"
            >
              <span className="text-sm font-semibold uppercase">
                {section.title}
              </span>
              <ul className="mt-4 list-inside space-y-3">
                {section.items?.map(
                  (item) =>
                    item.href && (
                      <li key={item.title}>
                        {item.external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-muted-foreground transition-colors duration-150 hover:text-primary focus-visible:text-primary data-[active=true]:font-semibold data-[active=true]:text-primary"
                          >
                            {item.title}
                          </a>
                        ) : (
                          <a
                            href={item.href}
                            data-active={
                              item.href.includes('#')
                                ? undefined
                                : isLinkActive(item.href, pathname)
                                  ? 'true'
                                  : undefined
                            }
                            className="text-sm text-muted-foreground transition-colors duration-150 hover:text-primary focus-visible:text-primary data-[active=true]:font-semibold data-[active=true]:text-primary"
                          >
                            {item.title}
                          </a>
                        )}
                      </li>
                    )
                )}
              </ul>
            </div>
          ))}
        </div>
      </Container>

      <div className="border-t py-8">
        <Container className="px-4">
          <p className="mb-6 max-w-5xl text-left text-muted-foreground text-xs leading-5">
            {m.footer_disclaimer({}, messageOptions)}
          </p>
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row">
            <span className="text-muted-foreground text-sm">
              &copy; {new Date().getFullYear()} {websiteConfig.metadata?.name}.{' '}
              {m.footer_rights_reserved({}, messageOptions)}
            </span>
            {languageLocales.length > 1 ? (
              <nav aria-label={m.common_switch_language({}, messageOptions)}>
                <ul className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                  {languageLocales.map((languageLocale) => (
                    <li key={languageLocale}>
                      {languageLocale === locale ? (
                        <span
                          aria-current="page"
                          className="font-semibold text-foreground"
                        >
                          {localeConfig[languageLocale].name}
                        </span>
                      ) : (
                        <a
                          href={localizeHref(canonicalPath, {
                            locale: languageLocale,
                          })}
                          hrefLang={localeConfig[languageLocale].hreflang}
                          lang={localeConfig[languageLocale].hreflang}
                          className="text-muted-foreground transition-colors duration-150 hover:text-primary focus-visible:text-primary"
                        >
                          {localeConfig[languageLocale].name}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </nav>
            ) : null}
          </div>
        </Container>
      </div>
    </footer>
  );
}
